/**
 * §4.3's capability table, and §4.4's role baselines, imported rather than
 * copied.
 *
 * `packages/shared/src/permissions/capabilities.ts` is the TypeScript mirror of
 * `role_baseline()` in the database, and its own test asserts the two are
 * identical in both directions. A third copy in the admin shell would be the
 * one nothing checks — and it would be the copy a person reads when they are
 * trying to work out why a button is greyed out.
 *
 * The module is type-only against `@matchbox/db`, so nothing from the server
 * side reaches the browser bundle.
 */

import {
  CAPABILITIES,
  type Capability,
  roleHasCapability,
} from '@matchbox/shared/permissions/capabilities.ts';
import type { UserRole } from '../api/types.ts';

export type { Capability };
export { CAPABILITIES, roleHasCapability };

/** The "Meaning" column of §4.3, so a picker never shows a bare dotted string. */
export const CAPABILITY_MEANING: Readonly<Record<Capability, string>> = {
  'document.view': "Read a node's content",
  'document.comment': 'Add comments',
  'document.suggest': 'Propose changes without edit rights',
  'document.edit': 'Modify content',
  'document.publish': 'Move a node to a client space',
  'document.approve': 'Act as a reviewer',
  'file.upload': 'Add or replace stored bytes',
  'file.delete': 'Remove stored bytes',
  'node.create': 'Create nodes of any type in a space',
  'node.delete': 'Soft-delete a node and its subtree',
  'space.manage': 'Create, rename, restructure standalone spaces',
  'space.grant': 'Grant access to others',
  'person.view': 'Look people up in the directory',
  'user.manage': 'Create accounts and change roles',
  'user.deactivate': 'Run the offboarding flow',
  'org.manage': "Create and edit organizations, including each org's own space",
  'admin.override': 'Force-publish, reassign ownership',
  'audit.view': 'Read the activity log',
  // Phase 2 §6.
  'project.manage': "Create and edit projects, including each project's paired spaces",
  'project.view': 'See a project record and its dashboard',
};

export const CAPABILITY_OPTIONS = CAPABILITIES.map((capability) => ({
  value: capability,
  label: capability,
  description: CAPABILITY_MEANING[capability],
}));

export function isKnownCapability(value: string): value is Capability {
  return (CAPABILITIES as readonly string[]).includes(value);
}

/**
 * §4.4 — "A role baseline grants a capability **only where the person also has
 * a grant reaching the node**." This answers the first half only. It is used to
 * explain a greyed-out control and to annotate a grant whose capability the
 * principal's role does not carry; it authorizes nothing, and the API resolves
 * every mutation against the database regardless of what this returns.
 */
export function roleCarries(role: UserRole, capability: string): boolean {
  return isKnownCapability(capability) && roleHasCapability(role, capability);
}
